/**
 * ALL IS WELL EXPERIENCE™ — Booking Request Utility
 *
 * Modular architecture: currently WEBSITE → SUPABASE → WHATSAPP → HUMAN CONFIRMATION
 * Future: WEBSITE → SECURE BACKEND → ALL IS WELL APPOINTMENT SYSTEM → WHATSAPP + SMS
 *
 * IMPORTANT: This is a PROTOTYPE booking-request workflow.
 * Do NOT claim appointment is confirmed. This is a REQUEST only.
 */

import { hospitalConfig } from '@/data/hospitalConfig';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ConsultationBookingRequest {
  requestId: string;
  patientName: string;
  patientMobile: string;
  patientEmail?: string;
  centreName: string;
  centreId?: string;
  department: string;
  departmentId?: string;
  doctorName: string;
  doctorId?: string;
  preferredDate: string;   // formatted display string e.g. "18 August 2026"
  preferredTime: string;   // formatted display string e.g. "4:00 PM"
  createdAt: string;
}

export interface DiagnosticsEnquiryRequest {
  requestId: string;
  patientName: string;
  patientMobile: string;
  patientEmail?: string;
  centreName: string;
  testName: string;
  category: string;
  createdAt: string;
}

export interface BookingPersistResult {
  success: boolean;
  requestId: string;
  error?: string;
  isDuplicate?: boolean;
}

// ─── Request ID Generator ─────────────────────────────────────────────────────

/**
 * Generates a unique booking request ID in the format: AIW-REQ-DDMMYY-XXXXX
 * Example: AIW-REQ-180826-00452
 */
export function generateRequestId(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const seq = String(Math.floor(Math.random() * 99999) + 1).padStart(5, '0');
  return `AIW-REQ-${dd}${mm}${yy}-${seq}`;
}

// ─── Supabase Persistence ─────────────────────────────────────────────────────

/**
 * Saves a consultation booking request to Supabase.
 * Prevents duplicates by checking for existing request_id.
 * Links to authenticated patient if logged in.
 * Status is always PENDING_CONFIRMATION — never confirmed.
 */
export async function persistBookingRequest(
  req: ConsultationBookingRequest,
  patientId?: string
): Promise<BookingPersistResult> {
  try {
    const supabase = createClient();

    // Duplicate prevention: check if this request_id already exists
    const { data: existing } = await supabase
      .from('booking_requests')
      .select('id')
      .eq('request_id', req.requestId)
      .maybeSingle();

    if (existing) {
      return { success: true, requestId: req.requestId, isDuplicate: true };
    }

    const { error } = await supabase.from('booking_requests').insert({
      request_id: req.requestId,
      patient_id: patientId ?? null,
      patient_name: req.patientName,
      patient_mobile: req.patientMobile,
      centre_id: req.centreId ?? req.centreName,
      department_id: req.departmentId ?? req.department,
      doctor_id: req.doctorId ?? req.doctorName,
      preferred_date: req.preferredDate,
      preferred_time: req.preferredTime,
      status: 'PENDING_CONFIRMATION',
    });

    if (error) {
      console.error('Booking persist error:', error.message);
      return { success: false, requestId: req.requestId, error: error.message };
    }

    return { success: true, requestId: req.requestId };
  } catch (err: any) {
    console.error('Booking persist exception:', err?.message);
    return { success: false, requestId: req.requestId, error: err?.message };
  }
}

// ─── WhatsApp Message Builders ────────────────────────────────────────────────

/**
 * Builds a professionally formatted consultation booking request message.
 * Only minimum booking information — no sensitive medical data.
 */
export function buildConsultationWhatsAppMessage(req: ConsultationBookingRequest): string {
  return `Hello All Is Well Team,

I would like to request a consultation appointment.

NEW CONSULTATION REQUEST
━━━━━━━━━━━━━━━━

Request ID:
${req.requestId}

PATIENT
Name: ${req.patientName}
Mobile: ${req.patientMobile}

CONSULTATION
Centre: ${req.centreName}
Department: ${req.department}
Doctor: ${req.doctorName}

PREFERRED SLOT
Date: ${req.preferredDate}
Time: ${req.preferredTime}

Please check availability and confirm.

━━━━━━━━━━━━━━━━
Sent from All Is Well Experience™`;
}

/**
 * Builds a professionally formatted diagnostics enquiry message.
 * Only minimum enquiry information — no sensitive medical data.
 */
export function buildDiagnosticsWhatsAppMessage(req: DiagnosticsEnquiryRequest): string {
  return `Hello All Is Well Team,

I would like to enquire about a diagnostic test.

NEW DIAGNOSTICS ENQUIRY
━━━━━━━━━━━━━━━━

Request ID:
${req.requestId}

PATIENT
Name: ${req.patientName}
Mobile: ${req.patientMobile}

ENQUIRY
Centre: ${req.centreName}
Test: ${req.testName}
Category: ${req.category}

Please provide availability and pricing information.

━━━━━━━━━━━━━━━━
Sent from All Is Well Experience™`;
}

/**
 * Opens the official All Is Well WhatsApp with a pre-filled message.
 * Uses the centralized officialWhatsAppLink from hospitalConfig.
 */
export function openWhatsAppWithMessage(message: string): void {
  const url = `${hospitalConfig.officialWhatsAppLink}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

// ─── Future Architecture Interfaces ──────────────────────────────────────────
// These interfaces prepare for the future backend integration.
// Currently unused — will be connected to the All Is Well appointment system.

export interface BookingApiPayload {
  requestId: string;
  type: 'consultation' | 'diagnostics' | 'pharmacy' | 'health-checkup' | 'centre-enquiry' | 'follow-up';
  patientDetails: {
    name: string;
    mobile: string;
    email?: string;
  };
  serviceDetails: Record<string, string>;
  preferredSlot?: {
    date: string;
    time: string;
  };
  centreId: string;
  createdAt: string;
}
